import { useCallback, useEffect, useState } from "react";

import CHECKOUT_MUTATIONS from "@/components/organisations/checkout/forms/CheckoutMutations";
import { PAYEE_ORGANISATION, PAYEE_PATIENT } from "@/components/organisations/constants";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import {
  BASKET_INVALID_CYREX_BUNDLE_STOCK_BASKET,
  BASKET_INVALID_CYREX_STORED_SAMPLE_STOCK_BASKET,
  BASKET_INVALID_RESTRICTED_SHIPPING
} from "@/core/constants";
import {
  ADD_ON_TO_PRODUCT_MUTATION,
  ADD_PRODUCT_TO_ORGANISATION_BASKET_MUTATION,
  ADD_SUPPLEMENT_TO_ORGANISATION_BASKET_MUTATION,
  CLEAR_BASKET_MUTATION,
  REMOVE_ADD_ON_FROM_PRODUCT_MUTATION,
  REMOVE_PRODUCT_FROM_ORGANISATION_BASKET_MUTATION,
  REMOVE_SUPPLEMENT_FROM_ORGANISATION_BASKET_MUTATION,
  SET_PRODUCT_QTY_IN_ORGANISATION_BASKET_MUTATION,
  SET_CYREX_PRODUCT_OPTION_FOR_BASKET_MUTATION,
  SET_SUPPLEMENT_QTY_IN_ORGANISATION_BASKET_MUTATION,
  UPDATE_ORGANISATION_BASKET_DETAILS_MUTATION,
  APPLY_DISCOUNT_MUTATION,
  REMOVE_DISCOUNT_MUTATION,
  TOGGLE_MULTIPLE_PATIENTS_FOR_BASKET_MUTATION,
  SET_PATIENT_ON_BASKET_LINE_MUTATION
} from "@/graphql/organisations/mutations";
import { ORGANISATION_BASKET_DETAIL_QUERY } from "@/graphql/organisations/queries/organisation";
import { useMutation, useQuery } from "@apollo/client";

export default function useOrganisationBasket() {
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState(null);
  const [
    acknowledgedBasketInvalidShippingRestrictionModal,
    setAcknowledgedBasketInvalidShippingRestrictionModal
  ] = useState(false);
  const [
    acknowledgedBasketMultipleProductsErrorModal,
    setAcknowledgedBasketMultipleProductsErrorModal
  ] = useState(false);
  const [acknowledgedBasketAddonErrorModal, setAcknowledgedBasketAddonErrorModal] = useState(false);
  const [acknowledgedBasketStockPatientErrorModal, setAcknowledgedBasketStockPatientErrorModal] =
    useState(false);
  const [
    acknowledgedBasketCyrexStoredSampleStockErrorModal,
    setAcknowledgedBasketCyrexStoredSampleStockErrorModal
  ] = useState(false);

  const hasMultipleProductsError = formErrors?.find(
    error =>
      error.field === "_All__" &&
      error.messages.includes(
        "Cannot purchase multiple products in a single basket if not purchasing as stock.  This includes options which share the same product."
      )
  );

  // this error is generated server side when they have addons in the basket and they are trying to purchase as stock
  const hasAddonError = formErrors?.find(
    error => error.field === "_All__" && error.messages.includes("Cannot convert to a stock order.")
  );

  const hasStockPatientError = formErrors?.find(
    error =>
      error.field === "purchaseAsStock" &&
      error.messages.includes("Purchase as stock must be purchased by organisation")
  );

  const { organisation } = useOrganisationContext();

  const { data: { basket } = {}, refetch: refetchBasket } = useQuery(
    ORGANISATION_BASKET_DETAIL_QUERY,
    {
      variables: { organisation: parseInt(organisation?.id) },
      skip: !organisation,
      fetchPolicy: "network-only",
      onCompleted: () => {},
      onError: error => {
        setError(error);
      }
    },
    [organisation]
  );

  const hasCyrexStoredSampleStockError = basket?.productLineItems?.some(
    l => l.invalidReason === BASKET_INVALID_CYREX_STORED_SAMPLE_STOCK_BASKET
  );

  const hasCyrexBundleStockError = basket?.productLineItems?.some(
    l => l.invalidReason === BASKET_INVALID_CYREX_BUNDLE_STOCK_BASKET
  );

  const hasCyrexStockOrderConflictError =
    hasCyrexStoredSampleStockError || hasCyrexBundleStockError;

  const mutations = CHECKOUT_MUTATIONS;

  // Check if Stripe onboarding is complete
  const isOnboardingComplete = organisation?.onboardingStatus === "complete";

  let mutation = mutations.paymentCheckoutSession;
  if (basket?.payee === "organisation") {
    if (organisation?.chargesEnabled) {
      mutation = mutations.paymentInvoice;
    }
  } else {
    mutation = mutations.patientOrder;
  }

  if (basket?.purchaseAsStock) {
    mutation = organisation?.chargesEnabled
      ? CHECKOUT_MUTATIONS.paymentInvoice
      : CHECKOUT_MUTATIONS.paymentCheckoutSession;
  }

  const [submitMutation, { error: submissionError, loading: submissionLoading }] = useMutation(
    mutation.mutation,
    {
      refetchQueries: [
        {
          // ensure that the client gets the new basket id after a successful checkout
          query: ORGANISATION_BASKET_DETAIL_QUERY,
          variables: {
            organisation: parseInt(organisation?.id)
          }
        }
      ]
    }
  );

  const hasProductNotSoldInLocaleErrorModal =
    basket?.invalid &&
    (basket?.productLineItems.some(l => l.invalidReason === BASKET_INVALID_RESTRICTED_SHIPPING) ||
      basket?.supplementLineItems.some(
        l => l.invalidReason === BASKET_INVALID_RESTRICTED_SHIPPING
      ));

  useEffect(() => {
    if (!hasProductNotSoldInLocaleErrorModal && acknowledgedBasketInvalidShippingRestrictionModal) {
      setAcknowledgedBasketInvalidShippingRestrictionModal(false);
    }
  }, [hasProductNotSoldInLocaleErrorModal, acknowledgedBasketInvalidShippingRestrictionModal]);

  const hasMultipleProductsErrorForModal =
    Boolean(hasMultipleProductsError) && !hasStockPatientError;

  useEffect(() => {
    if (!hasMultipleProductsErrorForModal && acknowledgedBasketMultipleProductsErrorModal) {
      setAcknowledgedBasketMultipleProductsErrorModal(false);
    }
  }, [hasMultipleProductsErrorForModal, acknowledgedBasketMultipleProductsErrorModal]);

  useEffect(() => {
    if (!hasAddonError && acknowledgedBasketAddonErrorModal) {
      setAcknowledgedBasketAddonErrorModal(false);
    }
  }, [hasAddonError, acknowledgedBasketAddonErrorModal]);

  useEffect(() => {
    if (!hasStockPatientError && acknowledgedBasketStockPatientErrorModal) {
      setAcknowledgedBasketStockPatientErrorModal(false);
    }
  }, [hasStockPatientError, acknowledgedBasketStockPatientErrorModal]);

  useEffect(() => {
    if (!hasCyrexStockOrderConflictError && acknowledgedBasketCyrexStoredSampleStockErrorModal) {
      setAcknowledgedBasketCyrexStoredSampleStockErrorModal(false);
    }
  }, [hasCyrexStockOrderConflictError, acknowledgedBasketCyrexStoredSampleStockErrorModal]);

  function handleErrors(responseData) {
    Object.keys(responseData).forEach(key => {
      if (responseData[key].errors && responseData[key].errors.length) {
        setError(responseData[key].errors[0].messages[0]);
      }
    });
  }

  const [_addProductToBasket, { loading: addProductToBasketMutationLoading }] = useMutation(
    ADD_PRODUCT_TO_ORGANISATION_BASKET_MUTATION,
    {
      onCompleted: data => {
        handleErrors(data);
      },
      onError: error => {
        setError(error);
      }
    }
  );

  const [
    _setCyrexProductOptionForBasket,
    { loading: setCyrexProductOptionForBasketMutationLoading }
  ] = useMutation(SET_CYREX_PRODUCT_OPTION_FOR_BASKET_MUTATION, {
    onCompleted: data => {
      handleErrors(data);
    },
    onError: error => {
      setError(error);
    }
  });

  const [discountErrors, setDiscountErrors] = useState([]);

  const [_applyDiscount, { loading: applyDiscountMutationLoading }] =
    useMutation(APPLY_DISCOUNT_MUTATION);
  const [_removeDiscount, { loading: removeDiscountMutationLoading }] =
    useMutation(REMOVE_DISCOUNT_MUTATION);
  const [_setPatientOnBasketLine, { loading: setPatientOnBasketLineMutationLoading }] = useMutation(
    SET_PATIENT_ON_BASKET_LINE_MUTATION
  );

  const [
    _setProductQtyInOrganisationBasket,
    { loading: setProductQtyInOrganisationBasketMutationLoading }
  ] = useMutation(SET_PRODUCT_QTY_IN_ORGANISATION_BASKET_MUTATION, {
    onCompleted: data => {
      handleErrors(data);
    },
    onError: error => {
      setError(error);
    }
  });

  const [
    _setSupplementQtyInOrganisationBasket,
    { loading: setSupplementQtyInOrganisationBasketMutationLoading }
  ] = useMutation(SET_SUPPLEMENT_QTY_IN_ORGANISATION_BASKET_MUTATION, {
    onCompleted: data => {
      handleErrors(data);
    },
    onError: error => {
      setError(error);
    }
  });

  const [_addOnToProductMutation, { loading: addOnToProductMutationLoading }] = useMutation(
    ADD_ON_TO_PRODUCT_MUTATION,
    {
      onCompleted: data => {
        handleErrors(data);
      },
      onError: error => {
        setError(error);
      }
    }
  );

  const [_removeProductFromBasket, { loading: removeProductFromBasketMutationLoading }] =
    useMutation(REMOVE_PRODUCT_FROM_ORGANISATION_BASKET_MUTATION, {
      onCompleted: data => {
        handleErrors(data);
      },
      onError: error => {
        setError(error);
      }
    });

  const [_clearBasketMutation, { loading: clearBasketMutationLoading }] = useMutation(
    CLEAR_BASKET_MUTATION,
    {
      onCompleted: data => {
        handleErrors(data);
        setFormErrors(null);
      },
      onError: error => {
        setError(error);
      }
    }
  );

  const [_removeAddOnFromProductMutation, { loading: removeAddOnFromProductMutationLoading }] =
    useMutation(REMOVE_ADD_ON_FROM_PRODUCT_MUTATION, {
      onCompleted: data => {
        handleErrors(data);
      },
      onError: error => {
        setError(error);
      }
    });

  const [_addSupplementToBasket, { loading: addSupplementToBasketMutationLoading }] = useMutation(
    ADD_SUPPLEMENT_TO_ORGANISATION_BASKET_MUTATION,
    {
      onCompleted: data => {
        handleErrors(data);
      },
      onError: error => {
        setError(error);
      }
    }
  );

  const [_removeSupplementFromBasket, { loading: removeSupplementFromBasketMutationLoading }] =
    useMutation(REMOVE_SUPPLEMENT_FROM_ORGANISATION_BASKET_MUTATION, {
      onCompleted: data => {
        handleErrors(data);
      },
      onError: error => {
        setError(error);
      }
    });

  const [_updateBasketDetails, { loading: updateBasketDetailsMutationLoading }] = useMutation(
    UPDATE_ORGANISATION_BASKET_DETAILS_MUTATION,
    {
      onCompleted: data => {
        handleErrors(data);
        setFormErrors(data?.updateBasketDetailsMutation?.errors || null);
      },
      onError: error => {
        setError(error);
        setFormErrors(null);
      }
    }
  );

  // If onboarding is not complete but the basket payee is "patient" (e.g. org was
  // previously onboarded and has a hanging patient basket), flip it to "organisation"
  // as that's the only valid payee without Stripe onboarding.
  useEffect(() => {
    if (basket && !isOnboardingComplete && basket.payee === PAYEE_PATIENT) {
      _updateBasketDetails({
        variables: {
          input: {
            id: basket.id,
            organisation: parseInt(organisation?.id),
            payee: PAYEE_ORGANISATION,
            purchaseAsStock: basket.purchaseAsStock,
            patient: basket.patient?.pk
          }
        }
      });
    }
  }, [basket, isOnboardingComplete, organisation, _updateBasketDetails]);

  const [
    toggleMultiplePatientsForBasketMutation,
    { loading: toggleMultiplePatientsForBasketMutationLoading }
  ] = useMutation(TOGGLE_MULTIPLE_PATIENTS_FOR_BASKET_MUTATION, {
    onCompleted: data => {
      handleErrors(data);
    },
    onError: error => {
      setError(error);
    }
  });

  const loading =
    submissionLoading ||
    addProductToBasketMutationLoading ||
    setCyrexProductOptionForBasketMutationLoading ||
    applyDiscountMutationLoading ||
    removeDiscountMutationLoading ||
    setPatientOnBasketLineMutationLoading ||
    setProductQtyInOrganisationBasketMutationLoading ||
    setSupplementQtyInOrganisationBasketMutationLoading ||
    addOnToProductMutationLoading ||
    removeProductFromBasketMutationLoading ||
    clearBasketMutationLoading ||
    removeAddOnFromProductMutationLoading ||
    addSupplementToBasketMutationLoading ||
    removeSupplementFromBasketMutationLoading ||
    updateBasketDetailsMutationLoading ||
    toggleMultiplePatientsForBasketMutationLoading;

  const applyDiscount = useCallback(
    discountCode => {
      if (!basket || !organisation || loading) {
        return;
      }
      _applyDiscount({
        variables: {
          input: {
            id: basket.id,
            organisation: parseInt(organisation.id),
            discountCode
          }
        }
      }).then(resp => {
        if (resp.data.applyDiscountMutation.errors.length) {
          setDiscountErrors(
            resp.data.applyDiscountMutation.errors.map(({ messages }) => messages[0])
          );
        } else {
          setDiscountErrors([]);
        }
      });
    },
    [_applyDiscount, organisation, basket, loading]
  );

  const removeDiscount = useCallback(() => {
    if (!basket || !organisation || loading) {
      return;
    }
    setDiscountErrors([]);
    _removeDiscount({
      variables: {
        input: {
          id: basket.id,
          organisation: parseInt(organisation.id)
        }
      }
    });
  }, [_removeDiscount, organisation, basket, loading]);

  const toggleMultiplePatientsForBasket = useCallback(
    v => {
      if (loading) return;
      toggleMultiplePatientsForBasketMutation({
        variables: {
          input: {
            id: basket?.id,
            organisation: parseInt(organisation?.id),
            multiplePatientsEnabled: v
          }
        }
      });
    },
    [basket, organisation, toggleMultiplePatientsForBasketMutation, loading]
  );

  const addProductToBasket = useCallback(
    (productId, productOptionId, clinicLocationId, kwargs = {}) => {
      if (loading) return;

      setError(null);

      const input = {
        id: basket.id,
        organisation: parseInt(organisation?.id),
        testProductCode: productId,
        testProductOption: productOptionId,
        clinicLocation: clinicLocationId
      };

      if (kwargs.patientId) {
        input.reportPatient = kwargs.patientId;
      }

      _addProductToBasket({
        variables: {
          input
        }
      });
    },
    [setError, basket, organisation, _addProductToBasket, loading]
  );

  const setCyrexProductOptionForBasket = useCallback(
    (testProductId, testProductOptionId, { patient, clearBasketPatient } = {}) => {
      if (!basket?.id || !organisation?.id) return;

      setError(null);

      const input = {
        id: basket.id,
        organisation: parseInt(organisation.id, 10),
        testProduct: testProductId,
        testProductOption: testProductOptionId
      };
      if (clearBasketPatient) {
        input.clearBasketPatient = true;
      } else if (patient != null && patient !== "" && typeof patient !== "boolean") {
        input.patient = patient;
      }

      _setCyrexProductOptionForBasket({
        variables: { input }
      });
    },
    [setError, basket, organisation, _setCyrexProductOptionForBasket]
  );

  const setProductQtyInOrganisationBasket = useCallback(
    ({ productId, productOptionId, clinicLocationId, quantity }) => {
      if (loading) return;
      if (quantity === "") return;

      setError(null);

      _setProductQtyInOrganisationBasket({
        variables: {
          input: {
            id: basket.id,
            organisation: parseInt(organisation?.id),
            testProduct: productId,
            testProductOption: productOptionId,
            clinicLocation: clinicLocationId,
            quantity: parseInt(quantity)
          }
        }
      });
    },
    [setError, basket, organisation, _setProductQtyInOrganisationBasket, loading]
  );

  const setSupplememtQtyInOrganisationBasket = useCallback(
    ({ supplementId, quantity }) => {
      if (loading) return;
      if (quantity === "") return;

      setError(null);

      _setSupplementQtyInOrganisationBasket({
        variables: {
          input: {
            id: basket.id,
            organisation: parseInt(organisation?.id),
            supplement: supplementId,
            quantity: parseInt(quantity)
          }
        }
      });
    },
    [setError, basket, organisation, _setSupplementQtyInOrganisationBasket, loading]
  );

  const addOnToProduct = useCallback(
    (productId, addOnId) => {
      if (loading) return;
      setError(null);

      _addOnToProductMutation({
        variables: {
          input: {
            id: basket.id,
            organisation: parseInt(organisation?.id),
            testProduct: productId,
            addon: addOnId
          }
        }
      });
    },
    [setError, basket, organisation, _addOnToProductMutation, loading]
  );

  const removeProductFromBasket = useCallback(
    (productId, productOptionId, clinicLocationId) => {
      if (loading) return;
      setError(null);

      _removeProductFromBasket({
        variables: {
          input: {
            id: basket.id,
            organisation: parseInt(organisation?.id),
            testProductCode: productId,
            testProductOption: productOptionId,
            clinicLocation: clinicLocationId
          }
        }
      });
    },
    [setError, basket, organisation, _removeProductFromBasket, loading]
  );

  const removeAddOnFromProduct = useCallback(
    (productId, addOnId) => {
      if (loading) return;
      setError(null);

      _removeAddOnFromProductMutation({
        variables: {
          input: {
            id: basket.id,
            organisation: parseInt(organisation?.id),
            testProduct: productId,
            addon: addOnId
          }
        }
      });
    },
    [setError, basket, organisation, _removeAddOnFromProductMutation, loading]
  );

  const clearBasket = useCallback(() => {
    if (loading) return;
    setError(null);

    _clearBasketMutation({
      variables: {
        input: {
          id: basket.id,
          organisation: parseInt(organisation?.id)
        }
      }
    });
  }, [setError, _clearBasketMutation, basket, organisation, loading]);

  const addSupplementToBasket = useCallback(
    supplementId => {
      if (loading) return;
      setError(null);

      _addSupplementToBasket({
        variables: {
          input: {
            id: basket.id,
            organisation: parseInt(organisation?.id),
            supplement: supplementId
          }
        }
      });
    },
    [setError, basket, organisation, _addSupplementToBasket, loading]
  );

  const removeSupplementFromBasket = useCallback(
    supplementId => {
      if (loading) return;
      setError(null);

      _removeSupplementFromBasket({
        variables: {
          input: {
            id: basket.id,
            organisation: parseInt(organisation?.id),
            supplement: supplementId
          }
        }
      });
    },
    [setError, basket, organisation, _removeSupplementFromBasket, loading]
  );

  const updateBasketDetails = useCallback(
    fields => {
      if (!basket || !organisation || loading) return;

      setError(null);

      _updateBasketDetails({
        variables: {
          input: {
            id: basket.id,
            organisation: parseInt(organisation?.id),
            // default field values to prevent overwrite
            payee: basket.payee,
            purchaseAsStock: basket.payee === PAYEE_PATIENT ? false : basket.purchaseAsStock,
            patient: basket.patient?.pk,
            ...fields
          }
        }
      });
    },
    [setError, basket, organisation, _updateBasketDetails, loading]
  );

  const hasBasketGotProduct = useCallback(
    (productId, productOptionId) => {
      if (!basket) {
        return false;
      }
      if (productId && productOptionId) {
        return basket.productLineItems.some(
          productLineItem =>
            productLineItem.product.id === productId &&
            productLineItem.productOption?.id === productOptionId
        );
      }
      return basket.productLineItems.some(
        productLineItem => productLineItem.product.id === productId
      );
    },
    [basket]
  );

  const hasProductGotAddon = useCallback(
    (productId, addOnId) => {
      if (!basket) {
        return false;
      }
      const productLine = basket.productLineItems.find(
        productLineItem => productLineItem.product.id === productId
      );
      if (productLine) {
        return productLine.addons.some(addonLineItem => addonLineItem.product.id === addOnId);
      }
      return false;
    },
    [basket]
  );

  const getQtyForProduct = useCallback(
    (productId, productOptionId) => {
      if (!basket) {
        return undefined;
      }
      const productLine = basket.productLineItems.find(productLineItem => {
        if (productId && productOptionId) {
          return (
            productLineItem.product.id === productId &&
            productLineItem.productOption?.id === productOptionId
          );
        } else if (productId) {
          return productLineItem.product.id === productId;
        }
        return undefined;
      });
      if (productLine) {
        return productLine.quantity;
      }
      return undefined;
    },
    [basket]
  );

  const hasMultipleProducts = useCallback(() => {
    if (!basket) {
      return false;
    }

    return Object.values(
      basket.productLineItems.reduce((summary, productLineItem) => {
        if (productLineItem.product.id in summary) {
          summary[productLineItem.product.id] += productLineItem.quantity;
        } else {
          summary[productLineItem.product.id] = 1;
        }
        return summary;
      }, {})
    ).some(qty => qty > 1);
  }, [basket]);

  const setPatientOnBasketLine = useCallback(
    ({ id, patient }) => {
      if (!basket || !organisation || loading) {
        return;
      }

      _setPatientOnBasketLine({
        variables: {
          input: {
            id,
            patient,
            basket: basket?.id,
            organisation: parseInt(organisation?.id)
          }
        }
      });
    },
    [basket, organisation, _setPatientOnBasketLine, loading]
  );

  const hasUndismissedBasketErrorModal =
    (Boolean(hasMultipleProductsError) &&
      !hasStockPatientError &&
      !acknowledgedBasketMultipleProductsErrorModal) ||
    (!!hasAddonError && !acknowledgedBasketAddonErrorModal) ||
    (!!hasStockPatientError && !acknowledgedBasketStockPatientErrorModal) ||
    (Boolean(hasCyrexStockOrderConflictError) &&
      !acknowledgedBasketCyrexStoredSampleStockErrorModal) ||
    (Boolean(hasProductNotSoldInLocaleErrorModal) &&
      !acknowledgedBasketInvalidShippingRestrictionModal);

  return {
    loading,
    error,
    basket,
    addProductToBasket,
    setCyrexProductOptionForBasket,
    removeProductFromBasket,
    addSupplementToBasket,
    removeSupplementFromBasket,
    updateBasketDetails,
    addOnToProduct,
    hasBasketGotProduct,
    hasProductGotAddon,
    removeAddOnFromProduct,
    getQtyForProduct,
    hasMultipleProductsError: hasMultipleProductsError && !hasStockPatientError,
    setFormErrors,
    clearBasket,
    hasMultipleProducts,
    hasAddonError,
    hasCyrexBundleStockError,
    hasCyrexStockOrderConflictError,
    hasCyrexStoredSampleStockError,
    hasStockPatientError,
    hasProductNotSoldInLocaleErrorModal,
    refetchBasket,
    setProductQtyInOrganisationBasket,
    setSupplememtQtyInOrganisationBasket,
    setAcknowledgedBasketInvalidShippingRestrictionModal,
    acknowledgedBasketInvalidShippingRestrictionModal,
    setAcknowledgedBasketMultipleProductsErrorModal,
    acknowledgedBasketMultipleProductsErrorModal,
    setAcknowledgedBasketAddonErrorModal,
    acknowledgedBasketAddonErrorModal,
    setAcknowledgedBasketStockPatientErrorModal,
    acknowledgedBasketStockPatientErrorModal,
    setAcknowledgedBasketCyrexStoredSampleStockErrorModal,
    acknowledgedBasketCyrexStoredSampleStockErrorModal,
    hasUndismissedBasketErrorModal,
    applyDiscount,
    removeDiscount,
    discountErrors,
    toggleMultiplePatientsForBasket,
    setPatientOnBasketLine,
    submissionError,
    submitMutation,
    mutation
  };
}
